import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** Aviso legal y RGPD, portado de la web mundoatletismo. Adaptado a los
 *  tokens --ld-* de Podios Stories. El responsable aparece como "S.M.B.". */
function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h3
      style={{
        fontSize: 14,
        marginTop: 20,
        marginBottom: 8,
        color: 'var(--ld-text)',
        fontFamily: 'var(--ld-font-display, serif)',
        fontWeight: 700,
      }}
    >
      {children}
    </h3>
  );
}

export function LegalNotice() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '32px 4px 8px' }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          style={{
            padding: 0,
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--ld-text-faint)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            opacity: 0.8,
            transition: 'opacity 120ms ease',
            fontFamily: 'var(--ld-font-sans, inherit)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.8'; }}
        >
          Aviso legal y datos
        </button>
      </div>

      {open && <LegalModal onClose={() => setOpen(false)} />}
    </>
  );
}

function LegalModal({ onClose }: { onClose: () => void }) {
  const linkStyle = { color: 'var(--ld-accent)', textDecoration: 'underline' };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-notice-title"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 1200,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--ld-surface)',
          color: 'var(--ld-text)',
          maxWidth: 720,
          maxHeight: '90vh',
          width: '100%',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
          padding: 28,
          borderRadius: 'var(--ld-radius-lg)',
          border: '1px solid var(--ld-line)',
          fontFamily: 'var(--ld-font-sans, system-ui, sans-serif)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 18,
            paddingBottom: 14,
            borderBottom: '1px solid var(--ld-line)',
            gap: 12,
          }}
        >
          <h2
            id="legal-notice-title"
            style={{
              margin: 0,
              fontSize: 22,
              fontFamily: 'var(--ld-font-display, serif)',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: 'var(--ld-text)',
              textTransform: 'uppercase',
            }}
          >
            Aviso legal y protección de datos
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--ld-text-muted)',
              background: 'transparent',
              border: '1px solid var(--ld-line)',
              borderRadius: 'var(--ld-radius-md)',
              cursor: 'pointer',
              fontFamily: 'var(--ld-font-sans, inherit)',
            }}
          >
            Cerrar
          </button>
        </div>

        <div style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--ld-text-muted)' }}>
          <p style={{ marginTop: 0 }}>
            Sitio no oficial de análisis estadístico de atletismo, sin fines
            lucrativos. La información mostrada es una elaboración personal a
            partir de los rankings y resultados públicos publicados por la{' '}
            <a href="https://atletismorfea.es/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              Real Federación Española de Atletismo (RFEA)
            </a>{' '}
            y la{' '}
            <a href="https://www.atletismomadrid.com" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              Federación de Atletismo de Madrid (FAM)
            </a>
            . Este sitio no tiene ninguna vinculación con la RFEA, la FAM ni con
            World Athletics.
          </p>

          <SectionHeading>Responsable</SectionHeading>
          <p>
            Responsable del sitio y del tratamiento: S.M.B., titular de
            mundoatletismo.es.
          </p>
          <p>
            Contacto:{' '}
            <a href="mailto:barallat@gmail.com" style={linkStyle}>
              barallat@gmail.com
            </a>
          </p>

          <SectionHeading>Titularidad y uso de los datos</SectionHeading>
          <p>
            Los datos de base (rankings, resultados, récords, calendarios y
            demás información estadística) son propiedad de la RFEA y de sus
            federaciones autonómicas. Se reutilizan aquí con fines
            estadísticos, divulgativos y aficionados, citando la fuente
            original. Este sitio no comercializa, no redistribuye en bloque y
            no ofrece servicios de pago con dicha información, ni suplanta a
            la fuente oficial. Para cualquier uso oficial, consulte
            directamente la{' '}
            <a href="https://atletismorfea.es/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              web de la RFEA
            </a>{' '}
            y su{' '}
            <a href="https://atletismorfea.es/aviso-legal" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              aviso legal
            </a>
            . Si la RFEA, la FAM o cualquier titular de los datos lo solicita,
            se retirará de inmediato el contenido afectado.
          </p>

          <SectionHeading>Sostenimiento del sitio</SectionHeading>
          <p>
            El sitio se mantiene de forma altruista y supone únicamente un
            gasto para su titular (dominio y alojamiento). Actualmente no
            existe ningún mecanismo de donación activo, por lo que el sitio no
            genera ningún ingreso. En caso de habilitarse en el futuro, las
            donaciones voluntarias se destinarían exclusivamente a cubrir
            dichos costes y no constituirían contraprestación por el acceso a
            los contenidos.
          </p>

          <SectionHeading>Tratamiento de datos personales (RGPD)</SectionHeading>
          <p>
            Aunque los datos provienen de fuentes públicas, su reutilización
            constituye un tratamiento de datos personales sujeto al Reglamento
            (UE) 2016/679 (RGPD) y a la LOPDGDD 3/2018.
          </p>
          <p>
            Base jurídica: interés legítimo (art. 6.1.f RGPD), consistente en
            la difusión y el análisis estadístico no oficial del atletismo
            español, información de relevancia en el ámbito deportivo. Se ha
            realizado una ponderación entre este interés y los derechos de las
            personas afectadas, reforzando las garantías en el caso de menores
            (ver más abajo).
          </p>
          <p>
            Datos tratados: nombre y apellidos, club, prueba, marca, fecha,
            lugar y, cuando consta en la fuente, año de nacimiento y país de
            nacionalidad deportiva.
          </p>
          <p>
            Conservación: los datos se mantienen mientras el sitio permanezca
            activo y la información conserve valor estadístico o histórico, o
            hasta que la persona afectada ejerza su derecho de supresión u
            oposición.
          </p>
          <p>
            No se elaboran perfiles individuales con fines comerciales ni se
            ceden los datos a terceros.
          </p>

          <SectionHeading>Menores de edad</SectionHeading>
          <p>
            Este sitio aplica medidas reforzadas para los datos de atletas
            menores de edad, conforme al tratamiento especialmente protegido
            que el RGPD reconoce a los menores:
          </p>
          <ul style={{ margin: '8px 0 12px 20px', padding: 0 }}>
            <li style={{ marginBottom: 6 }}>
              Para todos los menores de 18 años no se publica la fecha de
              nacimiento exacta, sino únicamente el año.
            </li>
            <li style={{ marginBottom: 6 }}>
              Para las categorías Sub-8, Sub-10, Sub-12 y Sub-14 no se habilita
              el acceso a la ficha personal individual (historial, percentiles
              y análisis estadístico ampliado); estos atletas únicamente
              aparecen, en su caso, dentro de los listados de ranking.
            </li>
            <li style={{ marginBottom: 6 }}>
              Las fichas personales de menores que sí se muestran no son
              indexables por los motores de búsqueda.
            </li>
          </ul>
          <p>
            Cualquier menor incluido en los listados, o su padre, madre o
            tutor legal, puede solicitar la retirada o limitación de sus
            datos, que se atenderá de forma prioritaria.
          </p>

          <SectionHeading>Cookies y medición de tráfico</SectionHeading>
          <p>
            Este sitio utiliza Google Analytics para medir el tráfico de forma
            agregada (páginas vistas, navegador, país aproximado). No se
            almacena la dirección IP completa ni se realiza perfilado
            individual ni publicidad. La medición implica una transferencia de
            datos a Google conforme a su{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              política de privacidad
            </a>
            . Puede inhabilitarla instalando el{' '}
            <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              complemento de inhabilitación de Google Analytics
            </a>{' '}
            o bloqueando las cookies de terceros en su navegador.
          </p>

          <SectionHeading>
            Derechos (acceso, rectificación, supresión, oposición, limitación
            y portabilidad)
          </SectionHeading>
          <p>
            Cualquier persona incluida en los listados, o su padre, madre o
            tutor legal si es menor, puede ejercer estos derechos escribiendo
            a{' '}
            <a href="mailto:barallat@gmail.com" style={linkStyle}>
              barallat@gmail.com
            </a>
            , identificándose adecuadamente. Las solicitudes se atienden en un
            plazo máximo de 30 días. Asimismo, puede presentar una reclamación
            ante la Agencia Española de Protección de Datos (
            <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              www.aepd.es
            </a>
            ).
          </p>

          <SectionHeading>Propiedad intelectual de este sitio</SectionHeading>
          <p>
            Los cálculos, vistas, gráficos y elaboraciones propias son una
            transformación con fines divulgativos de datos de terceros. Los
            nombres, marcas y logotipos de la RFEA, la FAM y World Athletics
            pertenecen a sus respectivos titulares.
          </p>

          <SectionHeading>Limitación de responsabilidad</SectionHeading>
          <p style={{ marginBottom: 0 }}>
            La información se ofrece "tal cual", sin garantía de exactitud,
            completitud o vigencia. Para resultados oficiales, consulte
            directamente las webs de la RFEA y la FAM.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
